import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AccountLayout from 'pages/account/components/AccountLayout/AccountLayout';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { getAccounts, getAccountsArray } from 'shared/config/store/selectors/account';
import { RootState } from 'shared/config/store/store';
import { setCurrentAccount } from 'shared/config/store/reducers/accountsSlice';
import { FileFormat } from 'shared/model/types';
import { useGetAccountCardsQuery } from 'entities/card/api';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { ROUTES } from 'shared/const/routes';
import { useLazyGetAccountStatementQuery, downloadAccountStatement } from '../api/index';
import { useIntl } from 'react-intl';
import { Card, CStatus } from 'entities/card/model/types';
import MoveFundsModal from '../components/MoveFundsModal/MoveFundsModal';

const AccountPage = () => {
  const { id } = useParams();
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showError, showSuccess } = useMessage();
  const [getStatement] = useLazyGetAccountStatementQuery();
  const [showMoveFundsModal, setShowMoveFundsModal] = useState(false);

  const accounts = useSelector(getAccounts);
  const accountsArray = useSelector(getAccountsArray);
  const currentEmi = useSelector(getCurrentEmi);
  const routeAccount = id && accounts[id] ? accounts[id] : undefined;
  const accountIdNum = id ? Number(id) : NaN;

  useGetAccountCardsQuery(
    { accountId: accountIdNum, emi: currentEmi },
    { skip: !Number.isFinite(accountIdNum) || !routeAccount }
  );

  const card = useSelector((state: RootState) => {
    if (!Number.isFinite(accountIdNum)) return undefined;
    const emi = getCurrentEmi(state);
    const byAccount = state.cards[emi]?.cards as Record<number, Card[]> | undefined;
    if (!byAccount) return undefined;
    const list = byAccount[accountIdNum] ?? byAccount[String(accountIdNum) as unknown as number];
    return list?.[0];
  });
  const canAddCard = !card || card.status === CStatus.Closed;
  
  const language = useSelector((state: RootState) => state.app.language);
  const userMeta = useSelector((state: RootState) => state.user.meta);

  // Show "Move funds" button only if user has more than one account
  const showMoveFunds = accountsArray.length > 1;

  useEffect(() => {
    if (!id || !accounts[id]) {
      navigate(-1);
      return;
    }
    dispatch(setCurrentAccount({ emi: currentEmi, accountId: Number(id) }));
  }, [id, accounts, navigate, dispatch, currentEmi]);

  const onStatementDownload = async ({
    dateRange,
    format,
  }: {
    dateRange: string[];
    format: 'pdf' | 'csv';
  }) => {
    try {
      await downloadAccountStatement(getStatement, {
        accountId: accountIdNum,
        dateRange: [dateRange[0], dateRange[1]],
        format: format.toUpperCase() as FileFormat,
        language: userMeta?.language || language || 'EN',
      });

      showSuccess(t({ id: 'accountPage.success' }));
    } catch (error) {
      console.error(error);
      showError(t({ id: 'accountPage.error' }));
    }
  };


  return (
    <>
      <AccountLayout
        color='linear-gradient(rgba(193, 227, 255, 1),rgba(212, 238, 238, 1))'
        title={routeAccount?.title ?? ''}
        iban={routeAccount?.iban ?? ''}
        status={routeAccount?.status}
        card={card}
        balance={routeAccount?.available ?? 0}
        canAddCard={canAddCard}
        onTopup={() => navigate(ROUTES.TOP_UP.path)}
        onMoveFunds={() => setShowMoveFundsModal(true)}
        showMoveFunds={showMoveFunds}
        onStatementDownload={onStatementDownload}
      />
      {showMoveFunds && (
        <MoveFundsModal
          open={showMoveFundsModal}
          onClose={() => setShowMoveFundsModal(false)}
          currentAccountId={accountIdNum}
        />
      )}
    </>
  );
};

export default AccountPage;
